import { useCallback, useEffect, useState, type ReactElement } from 'react';
import {
  Alert,
  Button,
  ContentLayout,
  Header,
  SpaceBetween,
  Tabs,
} from '@cloudscape-design/components';

import {
  getDrift,
  getRealm,
  listRealms,
  type DriftReport,
  type RealmDetail,
  type RealmRow,
} from '../api/ztrust';
import {
  DriftList,
  RealmDetails,
  RealmList,
} from '../components/ztrust/ZtrustReadPanels';
import { ZtrustRegistrationView } from '../components/ztrust/ZtrustRegistrationView';

export function ZtrustControl(): ReactElement {
  const [realms, setRealms] = useState<RealmRow[]>([]);
  const [drift, setDrift] = useState<DriftReport | undefined>();
  const [detail, setDetail] = useState<RealmDetail | undefined>();
  const [selectedRealm, setSelectedRealm] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  const load = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(undefined);
    try {
      const [realmResponse, driftResponse] = await Promise.all([
        listRealms(),
        getDrift(),
      ]);
      setRealms(realmResponse.realms);
      setDrift(driftResponse);
      setSelectedRealm((current) => current ?? realmResponse.realms[0]?.realm);
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : 'Unable to load ZTrust configuration',
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!selectedRealm) {
      setDetail(undefined);
      return;
    }
    let isCancelled = false;
    void getRealm(selectedRealm)
      .then((realmDetail) => {
        if (!isCancelled) setDetail(realmDetail);
      })
      .catch((detailError: unknown) => {
        if (!isCancelled) {
          setError(
            detailError instanceof Error
              ? detailError.message
              : 'Unable to load realm detail',
          );
        }
      });
    return () => {
      isCancelled = true;
    };
  }, [selectedRealm]);

  const highSeverityCount =
    drift?.findings.filter((finding) => finding.severity === 'high').length ?? 0;

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description="Read-only ZTrust state, declarative drift, and reviewed tenant-registration intent."
          actions={
            <Button iconName="refresh" onClick={() => void load()} loading={isLoading}>
              Refresh
            </Button>
          }
        >
          ZTrust Control
        </Header>
      }
    >
      <SpaceBetween size="l">
        {error ? (
          <Alert type="error" header="ZTrust control request failed">
            {error}
          </Alert>
        ) : null}
        {highSeverityCount > 0 ? (
          <Alert
            type="warning"
            header={`${highSeverityCount} high-severity configuration divergence${highSeverityCount === 1 ? '' : 's'}`}
          >
            Review the exact declared-versus-live differences before any approved Terraform run.
          </Alert>
        ) : null}
        <Tabs
          tabs={[
            {
              id: 'realms',
              label: 'Realms',
              content: (
                <RealmList
                  isLoading={isLoading}
                  realms={realms}
                  selectedRealm={selectedRealm}
                  onSelect={setSelectedRealm}
                />
              ),
            },
            {
              id: 'drift',
              label: `Drift${drift?.findings.length ? ` (${drift.findings.length})` : ''}`,
              content: <DriftList isLoading={isLoading} report={drift} />,
            },
            {
              id: 'detail',
              label: selectedRealm ? `Realm: ${selectedRealm}` : 'Realm detail',
              content: <RealmDetails detail={detail} />,
            },
            {
              id: 'registrations',
              label: 'Registration and approval',
              content: <ZtrustRegistrationView />,
            },
          ]}
        />
      </SpaceBetween>
    </ContentLayout>
  );
}
